import {css, StyleSheet} from 'aphrodite';
import React, {useRef} from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {EntityWithTextAndSuspended, MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch, UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import EditablePartyField from 'ui-library/inputs/EditablePartyField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {InputErrorMessageStyle} from 'ui-library/inputs/InputConstants';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import uniqueId from 'util/uniqueId';

interface BasePartiesFieldProps {
  value: Array<MinimalEntity>;
  onChange: (value: Array<MinimalEntity>) => void;
  label: string;
  showError: boolean;
  setDoesNotHaveUnresolvedInput?: (value: boolean) => void;
  errorText?: string;
  isDisabled?: boolean;
  dataTestAttrs?: DataTestAttrs;
  customViewerValue?: string | JSX.Element;
}

interface PartiesFieldPropsCreating extends BasePartiesFieldProps {
  isBeingCreated: true;
  setDoesNotHaveUnresolvedInput: (value: boolean) => void;
  errorText: string;
}

interface PartiesFieldPropsUpdating extends BasePartiesFieldProps {
  isBeingCreated: false;
}

type PartiesFieldProps = PartiesFieldPropsCreating | PartiesFieldPropsUpdating;

/**
 * Field to select or modify a list of parties. Renders EntityInputWithSearch when isBeingCreated is true and
 * EditablePartyField otherwise.
 */
export default function PartiesField(props: PartiesFieldProps): JSX.Element {
  const componentId = useRef(uniqueId());
  const entityToMinimalEntity = (entities: Array<EntityWithTextAndSuspended>): Array<MinimalEntity> =>
    entities ? entities.map((entity) => ({id: entity.id, handle: entity.handle})) : [];

  const sharedInputProps = {
    enableNewEntityInput: true,
    allowEmails: true,
    allowTeams: true,
    allowOrganizations: false,
    placeholder: 'Search for user or team',
    shouldAllowMultiple: true as const, // explicitly casting to true because PartyProps expects true (not boolean)
    userSearchType: UserSearchType.All,
  };

  return (
    <>
      <InputWrapper
        id={componentId.current}
        error={
          // Deliberate use of truthiness for `errorText` - we don't want an empty error message div if the
          // error text is empty
          props.showError && props.errorText
            ? {message: props.errorText, style: InputErrorMessageStyle.ERROR}
            : null
        }
        label={props.label}
        labelStyle={FieldLabel.STYLES.LABEL}
        isRequired={false}
        dataTestAttrs={{component: 'PartiesField', element: 'root'}}
      >
        {props.isBeingCreated ? (
          <EntityInputWithSearch
            {...sharedInputProps}
            onChange={(entityArray: Array<EntityWithTextAndSuspended>) => {
              props.onChange(entityToMinimalEntity(entityArray));
            }}
            onChangeInput={(value) => {
              props.setDoesNotHaveUnresolvedInput(value === '');
            }}
            value={props.value}
            size={TagsInputSize.SMALL}
            className={css(styles.inputBox)}
            emptyText="No results found"
            id={componentId.current}
          />
        ) : (
          <EditablePartyField
            {...sharedInputProps}
            isFullWidth={true}
            value={props.value}
            initialIsBeingEdited={false}
            isDisabled={Boolean(props.isDisabled)}
            onChange={(changedValue: Array<MinimalEntity>) => {
              props.onChange(changedValue);
            }}
            allowEmptyValue={true}
            chipSize={TagsInputSize.SMALL}
            dataTestAttrs={props.dataTestAttrs}
            customViewerValue={props.customViewerValue}
          />
        )}
      </InputWrapper>
    </>
  );
}

const styles = StyleSheet.create({
  inputBox: {
    flex: 1,
  },
});
